import { Box, Grid, IconButton, Paper, Table, TableBody, TableRow, Typography } from "@material-ui/core";
import TableCell from "@material-ui/core/TableCell";
import EditIcon from "@material-ui/icons/Edit";
import React, { useEffect } from "react";
import { useQuery } from "react-query";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";

import { Amount } from "../../../../components/amount";
import { Async } from "../../../../components/Async";
import { Center } from "../../../../components/center";
import { CreateButton } from "../../../../components/create_button";
import { ErrorPanel } from "../../../../components/error_panel";
import { ListTable } from "../../../../components/list_table";
import { Loading } from "../../../../components/loading";
import { QUERY_KEYS } from "../../../../constants";
import { IAsyncData } from "../../../../core/models";
import { isError, isPending } from "../../../../core/redux";
import { Routing } from "../../../../core/routing";
import { IAppReduxState } from "../../../../redux/store";
import { ROUTES } from "../../../../routes";
import { BudgetHistoryGraph } from "../../../budget_history/ui/components/budget_history_graph";
import { BudgetHistoryList } from "../../../budget_history/ui/components/budget_history_list";
import { BudgetHistoryListFilter } from "../../../budget_history/ui/components/budget_history_list_filter";
import { IClient } from "../../../client/data/entities";
import { ClientRepoImpl } from "../../../client/data/repo";
import { calcClientListSummary } from "../../../client/ui/utils";
import { ProductRepoImpl } from "../../../product/data/repo";
import { calcProductListSummary } from "../../../product/utils";
import { ETransactionType } from "../../../transaction/data/models";
import { TransactionRepoImpl } from "../../../transaction/data/repo";
import { calcTransactionListSummary } from "../../../transaction/utils";
import { IBudget } from "../../data/entities";
import { budgetRedux } from "../state/state";

const expenseTransactionListQuery = `?filter=type||$eq||${ETransactionType.EXPENSE}`;

export const BudgetListPage: React.FC = () => {
  const asyncProductList = useQuery(QUERY_KEYS.productList, () => ProductRepoImpl.getList());
  const asyncClientList = useQuery(QUERY_KEYS.clientList, () => ClientRepoImpl.getList());
  const asyncExpenseTransactionList = useQuery([QUERY_KEYS.transactionList, expenseTransactionListQuery], () =>
    TransactionRepoImpl.getList(expenseTransactionListQuery),
  );
  const dispatch = useDispatch();

  useEffect(() => {
    dispatch(budgetRedux.actions.getList());
  }, [dispatch]);
  const budgetListBranch = useSelector<IAppReduxState, IAsyncData<IBudget[]>>((state) => state.budget.list);
  const budget = budgetListBranch.data?.[0];
  const clientsWithDebt = asyncClientList.data?.filter((c) => c.balance < 0) ?? [];
  const clientsWithoutDebt = asyncClientList.data?.filter((c) => c.balance >= 0) ?? [];

  return (
    <Loading loading={isPending(budgetListBranch)}>
      <>
        {budget === undefined ? <CreateButton route={ROUTES.budget} /> : null}

        {budget !== undefined ? (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <Box mt={3}>
                  <Grid container justify="flex-end" alignItems="center">
                    <Box mr={1} component="span">
                      {budget && (
                        <IconButton to={Routing.generateEditRoute(ROUTES.budget, budget.id)} component={Link}>
                          <EditIcon />
                        </IconButton>
                      )}
                    </Box>
                  </Grid>

                  {isError(budgetListBranch) && (
                    <Center>
                      <Box py={2}>
                        <ErrorPanel branch={budgetListBranch} />
                      </Box>
                    </Center>
                  )}

                  <Loading loading={isPending(budgetListBranch)}>
                    <Paper>
                      <Table size="medium" className="detail-table">
                        <TableBody>
                          <TableRow>
                            <TableCell>Budce</TableCell>
                            <TableCell align="right">
                              <Amount amount={budget?.amount} />
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </Paper>
                  </Loading>
                </Box>

                <Box mt={1} />

                <Paper>
                  <Table size="medium" className="detail-table">
                    <TableBody>
                      <TableRow>
                        <TableCell>Anbar</TableCell>
                        <TableCell align="right">
                          <Async
                            asyncData={asyncProductList}
                            renderSuccess={(productList) => {
                              const productListSummary = calcProductListSummary(productList);
                              return <Amount amount={productListSummary.totalAmount} />;
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>

                <Box mt={1} />

                <ListTable<IClient>
                  branch={clientsWithDebt}
                  route={ROUTES.client}
                  renderHeader={() => (
                    <>
                      <TableCell>Ad</TableCell>
                      <TableCell align="right">Balans</TableCell>
                    </>
                  )}
                  renderRow={(client) => (
                    <>
                      <TableCell>{client.name}</TableCell>
                      <TableCell align="right">
                        <Amount amount={client.balance} />
                      </TableCell>
                    </>
                  )}
                  renderSummary={() => {
                    const total = calcClientListSummary(clientsWithDebt);

                    return (
                      <>
                        <TableCell colSpan={2}>Toplam</TableCell>
                        <TableCell colSpan={2} align="right">
                          <Amount amount={total} />
                        </TableCell>
                      </>
                    );
                  }}
                />

                <Box mt={1} />

                <ListTable<IClient>
                  branch={clientsWithoutDebt}
                  route={ROUTES.client}
                  renderHeader={() => (
                    <>
                      <TableCell>Ad</TableCell>
                      <TableCell align="right">Balans</TableCell>
                    </>
                  )}
                  renderRow={(client) => (
                    <>
                      <TableCell>{client.name}</TableCell>
                      <TableCell align="right">
                        <Amount amount={client.balance} />
                      </TableCell>
                    </>
                  )}
                  renderSummary={() => {
                    const total = calcClientListSummary(clientsWithoutDebt);

                    return (
                      <>
                        <TableCell colSpan={2}>Toplam</TableCell>
                        <TableCell colSpan={2} align="right">
                          <Amount amount={total} />
                        </TableCell>
                      </>
                    );
                  }}
                />

                <Box mt={1} />

                <Paper>
                  <Table size="medium" className="detail-table">
                    <TableBody>
                      <TableRow>
                        <TableCell>Xercler</TableCell>
                        <TableCell align="right">
                          <Async
                            asyncData={asyncExpenseTransactionList}
                            renderSuccess={(expenseTransactionList) => {
                              const transactionListSummary = calcTransactionListSummary(expenseTransactionList);
                              return <Amount amount={transactionListSummary.amount} />;
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </Paper>
              </Grid>
              {/* <Grid item xs={12} sm={12} md={6} lg={6}>
                <Box pt={7} />

                <BudgetHistoryListFilter />
                <Box pt={2} />
                <BudgetHistoryList />
              </Grid> */}
            </Grid>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <BudgetHistoryGraph />
              </Grid>
            </Grid>
          </>
        ) : (
          <Typography>Əməliyyatlara başlamaq üçün büdcəni yaradın.</Typography>
        )}
      </>
    </Loading>
  );
};
